"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useApp } from "../../contexts/AppContext"
import { motion, AnimatePresence } from "framer-motion"
import {
  BiPlusCircle,
  BiCog,
  BiPencil,
  BiTrash,
  BiCopy,
  BiToggleLeft,
  BiToggleRight,
  BiShow,
  BiData,
  BiHide,
  BiImport
} from "react-icons/bi"

import FeuilleTable from "../tables/FeuilleTable"
import FeuilleModal from "../modals/FeuilleModal"
import ConfirmAlert from "../alerts/ConfirmAlert"
import DropdownOption from "../ui/DropdownOption"

export default function FeuilleTabs({ classeurId, feuilles }) {
  const navigate = useNavigate()
  const { deleteFeuilleAPI, updateFeuilleAPI, addFeuilleAPI, getFeuilleLignes, getFeuilleDataAPI, loading } = useApp()

  // États pour les modals
  const [showInfo, setShowInfo] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editingFeuilleId, setEditingFeuilleId] = useState(null)
  const [deletingFeuilleId, setDeletingFeuilleId] = useState(null)
  const [activeTabFeuille, setActiveTabFeuille] = useState(null)
  const [activeTabColumns, setActiveTabColumns] = useState([])
  const [feuillesData, setFeuillesData] = useState([])
  const [activeTab, setActiveTab] = useState(localStorage.getItem("tab-feuille") || null)

  useEffect(() => {
    const tabExists = feuilles.some((f) => f.code_feuille == activeTab)
    if (!activeTab || !tabExists) setActiveTab(feuilles[0].code_feuille)
  }, [feuilles])

  useEffect(() => {
    const fetchData = async () => {
      let resultData = {}
      try {
        resultData = await getFeuilleDataAPI(activeTab)
      } catch (err) {
        console.error("Erreur chargement données feuille", activeTab, err)
      }
      
      setFeuillesData(resultData)
    }

    if (activeTab) fetchData()
    setActiveTabFeuille(feuilles.find((f) => f.code_feuille == activeTab))
    setActiveTabColumns(getFeuilleLignes(activeTab))
  }, [activeTab, setFeuillesData])

  const handleTabChange = (feuille) => {
    setActiveTab(feuille.code_feuille)
    localStorage.setItem("tab-feuille", feuille.code_feuille)
  }

  const handleDeleteFeuille = async () => {
    if (deletingFeuilleId) {
      try {
        await deleteFeuilleAPI(deletingFeuilleId)
        if (activeTab == deletingFeuilleId) {
          const remainingFeuilles = feuilles.filter((f) => f.code_feuille !== deletingFeuilleId)
          setActiveTab(remainingFeuilles[0]?.code_feuille)
          localStorage.removeItem("tab-feuille")
        }
      } catch (error) {
        console.error("Erreur lors de la suppression:", error)
      }
    }
  }

  const handleEditFeuille = (feuilleId) => {
    setEditingFeuilleId(feuilleId)
    setShowEditModal(true)
  }

  const handleDuplicateFeuille = async (feuille) => {
    try {
      const duplicatedFeuille = {
        ...feuille,
        nom_feuille: `${feuille.nom_feuille} (Copie)`,
        libelle_feuille: `Copie de ${feuille.libelle_feuille || feuille.nom_feuille}`,
        date_insertion: new Date().toISOString(),
      }
      delete duplicatedFeuille.code_feuille // Supprimer l'ID pour créer une nouvelle feuille
      await addFeuilleAPI(duplicatedFeuille)
    } catch (error) {
      console.error("Erreur lors de la duplication:", error)
    }
  }

  const handleToggleStatus = async (feuille) => {
    try {
      const updatedFeuille = {
        ...feuille,
        statut: feuille.statut == 1 ? 0 : 1,
      }
      await updateFeuilleAPI(feuille.code_feuille, updatedFeuille)
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error)
    }
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setEditingFeuilleId(null)
  }

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false)
    setDeletingFeuilleId(null)
  }

  // Variants d'animation
  const tabVariants = {
    inactive: { scaleX: 1, opacity: 0.7 },
    active: { scaleX: 1.05, opacity: 1 },
    hover: { scaleX: 1.06, opacity: 0.9 },
  }

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  }

  return (
    <div>
      {/* Onglets avec animations */}
      <div className="border-bottom mb-2">
        <ul className="nav nav-tabs border-0 position-relative bg-light" role="tablist">
          <AnimatePresence>
            {feuilles.map((feuille) => (
              <motion.li
                key={feuille.code_feuille}
                className="nav-item me-1"
                role="presentation"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                <motion.button
                  className={`nav-link position-relative rounded-top-1 px-3 py-1 shadow-sm border fw-semibold
                  ${activeTab == feuille.code_feuille ? "bg-secondary text-white active" : "bg-light text-secondary"}`}
                  onClick={() => handleTabChange(feuille)}
                  type="button"
                  role="tab"
                  variants={tabVariants}
                  initial="inactive"
                  animate={activeTab == feuille.code_feuille ? "active" : "inactive"}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 1 }}
                  disabled={loading}
                >
                  <div className="d-flex align-items-center small">
                    {feuille.icone && (
                      <i className={`bi bi-${feuille.icone} me-2`}></i>
                    )}
                    <span className="small">{feuille.nom_feuille}</span>
                    {feuille.statut == 0 && (
                      <span className="badge bg-secondary ms-2 small rounded-pill">
                        Inactive
                      </span>
                    )}
                  </div>

                  {/* Petite barre animée en dessous de l’onglet actif */}
                  {activeTab == feuille.code_feuille && (
                    <motion.div
                      layoutId="tab-indicator"
                      className="position-absolute bottom-0 start-0 w-100 rounded-0 bg-secondary"
                      style={{ height: "3px" }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </motion.button>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      </div>

      {/* Contenu de l'onglet actif */}
      <AnimatePresence mode="wait">
        {activeTabFeuille && (
          <motion.div
            key={activeTabFeuille.code_feuille}
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="tab-pane fade show active"
          >
            {/* En-tête de la feuille */}
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center">
                  {activeTabFeuille.icone && <i className={`bi bi-${activeTabFeuille.icone} me-2 text-primary fs-4`}></i>}
                  <h6 className="mb-0">{activeTabFeuille.libelle_feuille}</h6>
                  <span className={`badge ms-3 ${activeTabFeuille.statut == 1 ? "bg-success" : "bg-secondary"} rounded-pill`}>
                    {activeTabFeuille.statut == 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              {/* Actions de la feuille */}
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <DropdownOption options={[
                  {
                    label: "Modifier",
                    icon: <BiPencil />,
                    onClick: () => handleEditFeuille(activeTabFeuille.code_feuille),
                    className: "text-primary",
                  },
                  {
                    label: "Dupliquer",
                    icon: <BiCopy />,
                    onClick: () => handleDuplicateFeuille(activeTabFeuille),
                    className: "text-success",
                  },
                  {
                    label: activeTabFeuille.statut == 1 ? "Désactiver" : "Activer",
                    icon: activeTabFeuille.statut == 1 ? <BiToggleLeft /> : <BiToggleRight />,
                    onClick: () => handleToggleStatus(activeTabFeuille),
                    className: activeTabFeuille.statut == 1 ? "text-warning" : "text-success",
                  },
                  {
                    label: "Supprimer",
                    icon: <BiTrash />,
                    onClick: () => {
                      setDeletingFeuilleId(activeTabFeuille.code_feuille)
                      setShowDeleteConfirm(true)
                    },
                    className: "text-danger",
                  },
                ]} />

                <div className="btn-group btn-group-sm">
                  <button onClick={() => setShowInfo(!showInfo)} title="Informations"
                    className="btn btn-sm btn-outline-secondary py-1 d-flex align-items-center">
                    {showInfo ? <BiHide className="me-1 fs-6" /> : <BiShow className="me-1 fs-6" />} {showInfo ? "Masquer" : "Détails"}
                  </button>

                  <button onClick={() => navigate(`/feuille/${activeTabFeuille.code_feuille}/fields`)} title="Configuration champs"
                    className="btn btn-sm btn-outline-secondary py-1 d-flex align-items-center">
                    <BiCog className="me-1 fs-6" /> Champs
                  </button>

                  <button onClick={() => navigate(`/feuille/${activeTabFeuille.code_feuille}/data`)} title="Données"
                    className="btn btn-sm btn-outline-secondary py-1 d-flex align-items-center">
                    <BiData className="me-1 fs-6" /> Données
                  </button>
                </div>
              </div>
            </div>

            {/* Informations supplémentaires */}
            <AnimatePresence>
              {showInfo && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="card bg-light rounded-1 h-100">
                        <div className="card-body py-2">
                          {activeTabFeuille.note && <small className="text-muted">{activeTabFeuille.note}</small>}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row g-3">
                        <div className="col-sm-6 col-md-3">
                          <div className="card bg-light rounded-1">
                            <div className="card-body text-center py-2">
                              <BiCog className="text-success mb-1" />
                              <div className="small text-muted">Colonnes</div>
                              <strong>{activeTabColumns.length || 0}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-6 col-md-3">
                          <div className="card bg-light rounded-1">
                            <div className="card-body text-center py-2">
                              <BiData className="text-primary mb-1" />
                              <div className="small text-muted">Données</div>
                              <strong>{feuillesData.length || 0}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-6 col-md-3">
                          <div className="card bg-light rounded-1">
                            <div className="card-body text-center py-2">
                              <BiShow className="text-info mb-1" />
                              <div className="small text-muted">Lignes impression</div>
                              <strong>{activeTabFeuille.nb_ligne_impr || 0}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-6 col-md-3">
                          <div className="card bg-light rounded-1">
                            <div className="card-body text-center py-2">
                              <BiPlusCircle className="text-warning mb-1" />
                              <div className="small text-muted">Créée le</div>
                              <strong className="small">
                                {activeTabFeuille.date_insertion
                                  ? new Date(activeTabFeuille.date_insertion).toLocaleDateString("fr-FR")
                                  : "-"}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Table des données */}
            <FeuilleTable feuilleId={activeTabFeuille.code_feuille} classeurId={classeurId} feuilleData={feuillesData} setFeuillesData={setFeuillesData} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <FeuilleModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        classeurId={classeurId}
        feuilleId={editingFeuilleId}
      />

      <ConfirmAlert
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleDeleteFeuille}
        title="Supprimer la feuille"
        message={`Êtes-vous sûr de vouloir supprimer la feuille "${feuilles.find((f) => f.code_feuille == deletingFeuilleId)?.nom_feuille
          }" ? Cette action est irréversible et supprimera toutes les données associées.`}
        confirmText="Supprimer"
        variant="danger"
      />
    </div>
  )
}
